/**
 * Studio Preview — Shared seed data & catalogs
 * ---------------------------------------------
 * Sandbox demonstration data used by the studio preview components.
 * Kept in a plain JS module (no React imports) so it can be imported by
 * any per-tab file without risk of circular deps with StudioPreview.jsx.
 */

// Sandbox patients (Marie Lefèvre, Paul Garcia, Aïcha Benali) with the full
// enriched schema: allergies, ALDs, INS, ATCD (plain + CIM-10 coded), ROSP
// indicators, consultations (+ PMS status + NGAP codes), prescriptions,
// documents.
export const SEED_PATIENTS = [
  { id: "p1", first: "Marie", last: "Lefèvre",  age: 64, ins: "2 60 04 75 110 234/12", lastVisit: "12 mars 2026", allergies: ["Pénicilline"], ald: ["Diabète type 2"], isMT: true,  nextRdv: "2026-04-02T14:30", dmpLinked: true,
    sex: "F", birth: "04/06/1961", weight: 72, height: 162, bmi: 27.4, ta: "138/82", pulse: 76,
    antecedents: ["HTA essentielle (2018)", "Diabète type 2 (2020)", "Cholécystectomie (2015)"],
    antecedentsCoded: [
      { code: "I10", label: "HTA essentielle", since: "2018" },
      { code: "E11", label: "Diabète type 2", since: "2020" },
      { code: "K80.2", label: "Cholécystectomie (ATCD)", since: "2015" },
    ],
    rosp: { vaccinationUpToDate: false, screeningInProgress: true, chronicFollowUp: "late" },
    consultations: [
      { date: "12 mars 2026", motif: "Suivi diabète T2", diag: "Diabète déséquilibré (HbA1c 7.8%)", notes: "Ajustement Metformine 1000 mg x2/j. Glycémies à jeun >1.4g sur 7j. Conseils hygiéno-diététiques renforcés.", duration: 25, ps: "Dr. Martin", pmsSent: true, ngap: "G · 26,50 €" },
      { date: "15 février 2026", motif: "Renouvellement traitement", diag: "Diabète T2 + HTA stable", notes: "Renouvellement Metformine, Amlodipine. Bilan biologique demandé.", duration: 15, ps: "Dr. Martin", pmsSent: true, ngap: "G · 26,50 €" },
      { date: "8 janvier 2026", motif: "Téléconsultation grippe", diag: "Syndrome grippal sans gravité", notes: "Repos, paracétamol, hydratation. Arrêt 3 jours.", duration: 15, ps: "Dr. Martin", pmsSent: false, ngap: "TCG · 25,00 €" },
    ],
    prescriptions: [
      { date: "12 mars 2026", items: ["Metformine 1000 mg — 1 cp matin et soir x 90j", "Amlodipine 5 mg — 1 cp matin x 90j", "Atorvastatine 20 mg — 1 cp soir x 90j"], renewable: true },
      { date: "15 février 2026", items: ["Bandelettes glycémie x 1 boîte", "Lancettes x 1 boîte"], renewable: false },
    ],
    documents: [
      { date: "10 mars 2026", type: "Biologie", title: "Bilan HbA1c + glycémie à jeun + ionogramme", from: "Labo Sud Orléans", size: "182 Ko" },
      { date: "5 mars 2026", type: "CR hospitalisation", title: "Compte-rendu de consultation cardio", from: "CHU Orléans · Dr. Dubois", size: "94 Ko" },
      { date: "22 février 2026", type: "Imagerie", title: "Échographie abdominale", from: "Centre d'imagerie Loiret", size: "1.2 Mo" },
    ],
  },
  { id: "p2", first: "Paul",  last: "Garcia",    age: 47, ins: "1 79 06 75 110 987/45", lastVisit: "28 février 2026", allergies: [], ald: [], isMT: true, nextRdv: null, dmpLinked: false,
    sex: "M", birth: "12/06/1979", weight: 84, height: 178, bmi: 26.5, ta: "128/78", pulse: 68,
    antecedents: ["Lombalgie chronique", "Tabagisme actif (15 PA)"],
    antecedentsCoded: [
      { code: "M54.5", label: "Lombalgie chronique", since: "2019" },
      { code: "Z72.0", label: "Tabagisme actif (15 PA)", since: "2005" },
    ],
    rosp: { vaccinationUpToDate: true, screeningInProgress: false, chronicFollowUp: "n/a" },
    consultations: [
      { date: "28 février 2026", motif: "Consultation lombalgie", diag: "Lombalgie commune mécanique", notes: "Examen rassurant. Pas de drapeau rouge. Reprise activité progressive. Kiné prescrite.", duration: 20, ps: "Dr. Martin", pmsSent: false, ngap: "G · 26,50 €" },
      { date: "14 janvier 2026", motif: "Bilan annuel", diag: "Patient en bonne santé apparente", notes: "BMI 26.5. Conseils sevrage tabagique. Bilan bio annuel demandé.", duration: 30, ps: "Dr. Martin", pmsSent: true, ngap: "G · 26,50 €" },
    ],
    prescriptions: [
      { date: "28 février 2026", items: ["Paracétamol 1000 mg — si douleur x 7j", "Kinésithérapie : 12 séances rééducation lombaire"], renewable: false },
    ],
    documents: [
      { date: "26 février 2026", type: "Imagerie", title: "IRM lombaire", from: "Centre d'imagerie Loiret", size: "3.4 Mo" },
      { date: "14 janvier 2026", type: "Biologie", title: "Bilan annuel complet", from: "Labo Sud Orléans", size: "210 Ko" },
    ],
  },
  { id: "p3", first: "Aïcha", last: "Benali",    age: 38, ins: "2 88 10 13 215 442/89", lastVisit: "5 mars 2026", allergies: ["Arachides", "Latex"], ald: [], isMT: true,  nextRdv: "2026-04-01T10:00", dmpLinked: true,
    sex: "F", birth: "10/03/1988", weight: 58, height: 165, bmi: 21.3, ta: "118/72", pulse: 72,
    antecedents: ["Allergie alimentaire sévère (arachides)", "Asthme allergique modéré"],
    antecedentsCoded: [
      { code: "T78.0", label: "Allergie alimentaire sévère (arachides)", since: "2010" },
      { code: "J45.0", label: "Asthme allergique modéré", since: "2012" },
    ],
    rosp: { vaccinationUpToDate: true, screeningInProgress: true, chronicFollowUp: "up-to-date" },
    consultations: [
      { date: "5 mars 2026", motif: "Suivi asthme", diag: "Asthme contrôlé", notes: "Pic flow stable. Poursuite traitement de fond. Carte d'urgence anaphylaxie à jour.", duration: 20, ps: "Dr. Martin", pmsSent: true, ngap: "G · 26,50 €" },
      { date: "18 février 2026", motif: "Téléconsultation rhinite", diag: "Rhinite allergique", notes: "Cétirizine en cure courte. Saison pollinique débutée.", duration: 12, ps: "Dr. Martin", pmsSent: false, ngap: "TCG · 25,00 €" },
      { date: "20 décembre 2025", motif: "Renouvellement EpiPen", diag: "Allergie sévère aux arachides", notes: "Renouvellement adrénaline auto-injectable. Plan d'action écrit remis.", duration: 15, ps: "Dr. Martin", pmsSent: true, ngap: "G · 26,50 €" },
    ],
    prescriptions: [
      { date: "5 mars 2026", items: ["Budésonide/Formotérol 200/6 µg — 1 inhal matin et soir x 90j", "Salbutamol 100 µg — si crise"], renewable: true },
      { date: "20 décembre 2025", items: ["Adrénaline auto-injectable 0.3 mg (EpiPen) x 2", "Cétirizine 10 mg — si symptômes"], renewable: true },
    ],
    documents: [
      { date: "5 mars 2026", type: "Spirométrie", title: "EFR de contrôle", from: "Pneumologue · Dr. Roux", size: "320 Ko" },
      { date: "20 décembre 2025", type: "Plan d'action", title: "Plan d'action anaphylaxie personnalisé", from: "Allergologue · Dr. Khaled", size: "78 Ko" },
    ],
  },
];

// Drug autocomplete catalog for "Nouvelle ordonnance" (Ordonnances tab).
export const DRUG_CATALOG = [
  { name: "Amoxicilline 500 mg", class: "Antibiotique (bêta-lactamine)", allergyKeys: ["pénicilline", "penicilline", "amoxicilline"] },
  { name: "Amoxicilline / Ac. clavulanique 1 g", class: "Antibiotique (bêta-lactamine)", allergyKeys: ["pénicilline", "penicilline"] },
  { name: "Ibuprofène 400 mg", class: "AINS", allergyKeys: ["ains", "aspirine", "ibuprofène"] },
  { name: "Paracétamol 1000 mg", class: "Antalgique", allergyKeys: [] },
  { name: "Metformine 1000 mg", class: "Antidiabétique oral", allergyKeys: [] },
  { name: "Amlodipine 5 mg", class: "Anti-hypertenseur (IC)", allergyKeys: [] },
  { name: "Atorvastatine 20 mg", class: "Hypolipémiant (statine)", allergyKeys: [] },
  { name: "Cétirizine 10 mg", class: "Antihistaminique H1", allergyKeys: [] },
  { name: "Salbutamol 100 µg inhalation", class: "Bêta-2 mimétique", allergyKeys: [] },
  { name: "Latex (compresse stérile)", class: "Dispositif médical", allergyKeys: ["latex"] },
];

// CIM-10 catalog used by the patient "Antécédents" tab autocomplete.
export const CIM10_CATALOG = [
  { code: "I10", label: "HTA essentielle" },
  { code: "I25", label: "Cardiopathie ischémique chronique" },
  { code: "I48", label: "Fibrillation auriculaire" },
  { code: "E11", label: "Diabète type 2" },
  { code: "E10", label: "Diabète type 1" },
  { code: "E66", label: "Obésité" },
  { code: "E78", label: "Dyslipidémie" },
  { code: "J45", label: "Asthme" },
  { code: "J44", label: "BPCO" },
  { code: "K80", label: "Lithiase biliaire" },
  { code: "K80.2", label: "Cholécystectomie (ATCD)" },
  { code: "M54.5", label: "Lombalgie chronique" },
  { code: "M17", label: "Gonarthrose" },
  { code: "N18", label: "Insuffisance rénale chronique" },
  { code: "F32", label: "Épisode dépressif" },
  { code: "F41.1", label: "Anxiété généralisée" },
  { code: "T78.0", label: "Allergie alimentaire sévère" },
  { code: "Z72.0", label: "Tabagisme actif" },
  { code: "Z85", label: "ATCD de cancer" },
  { code: "R73", label: "Hyperglycémie / prédiabète" },
];

// NGAP / CCAM codes proposed by the PMS transmission modal + Paramétrage.
export const NGAP_CCAM_CODES = [
  { code: "G", label: "Consultation généraliste (25,00 €)" },
  { code: "GS", label: "Consultation généraliste secteur 2 (30,00 €)" },
  { code: "C", label: "Consultation spécialiste (30,00 €)" },
  { code: "APC", label: "Avis ponctuel de consultant (55,00 €)" },
  { code: "TCG", label: "Téléconsultation généraliste (25,00 €)" },
  { code: "TCM", label: "Téléconsultation spécialiste (30,00 €)" },
  { code: "CCX", label: "Consultation complexe (46,00 €)" },
  { code: "V", label: "Visite à domicile (35,00 €)" },
  { code: "MEP", label: "Majoration enfant préoccupante (5,00 €)" },
  { code: "MSH", label: "Majoration sortie hospitalière (30,00 €)" },
];

// ROSP indicators — CNAM 2026 baseline (15 official indicators).
export const ROSP_INDICATORS = [
  { id: "vacc", label: "Vaccination à jour" },
  { id: "screen", label: "Dépistage en cours" },
  { id: "chronic", label: "Suivi maladie chronique" },
  { id: "hba1c", label: "HbA1c annuelle (diabétiques)" },
  { id: "ldl", label: "LDL cholestérol (patients à risque)" },
  { id: "ta", label: "Tension artérielle (HTA)" },
  { id: "bmi", label: "IMC annuel" },
  { id: "smoking", label: "Statut tabagique documenté" },
  { id: "colon", label: "Dépistage cancer colorectal (50-74 ans)" },
  { id: "breast", label: "Mammographie (50-74 ans)" },
  { id: "cervix", label: "Frottis cervico-utérin (25-65 ans)" },
  { id: "prostate", label: "Dépistage prostate (info éclairée)" },
  { id: "iatro", label: "Iatrogénie · révision ordonnance annuelle" },
  { id: "sport", label: "Prescription activité physique adaptée" },
  { id: "aeg", label: "Anti-agrégants / anticoagulants suivi" },
];

// PMS software catalog for the "Établissement > PMS connecté" panel.
export const PMS_CATALOG = [
  { id: "medistory", name: "MediStory 4", vendor: "Prokov Éditions" },
  { id: "hellodoc", name: "HelloDOC", vendor: "Imagine Éditions" },
  { id: "weda", name: "Weda", vendor: "Weda SAS" },
  { id: "axisante", name: "AxiSanté 5", vendor: "Compugroup Medical" },
  { id: "medimust", name: "MediMust", vendor: "Silk Informatique" },
  { id: "custom", name: "Autre / interne", vendor: "—" },
];
